import React from "react";
import { Link } from "react-router-dom";
import { Compass } from "lucide-react";
import { useLang } from "@/lib/i18n";
import { L } from "@/lib/localize";
import { useAssets, tourCover } from "@/lib/assets";
import { imgSrc, imgSet } from "@/lib/img";
import { Reveal } from "./Reveal";

export const badgeClass = (badge) => {
  if (badge === "sold_out") return "font-mono text-[10px] uppercase tracking-[0.18em] px-3 py-1.5 bg-[#B71C1C]/85 text-cream";
  if (badge === "last_spots") return "font-mono text-[10px] uppercase tracking-[0.18em] px-3 py-1.5 bg-copper text-obsidian";
  return "font-mono text-[10px] uppercase tracking-[0.18em] px-3 py-1.5 bg-[#2E7D32]/85 text-cream";
};

export default function ExpeditionCard({ tour, index = 0 }) {
  const { t, lang } = useLang();
  const assets = useAssets();
  const lt = L(tour, lang);
  const cover = tourCover(assets, tour);
  return (
    <Reveal delay={(index % 4) * 0.08} className="h-full">
      <Link to={`/viaggi/${tour.slug}`} data-testid={`expedition-card-${tour.slug}`} className="group block h-full">
        <div className="relative overflow-hidden border border-white/5 group-hover:border-copper/50 transition-colors duration-500">
          <div className="aspect-[4/3] overflow-hidden">
            {cover ? (
              <img
                src={imgSrc(cover, 800)}
                srcSet={imgSet(cover, [400, 800, 1200])}
                sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw"
                alt={lt.title}
                className="w-full h-full object-cover transition-transform duration-[1400ms] ease-out group-hover:scale-105"
                loading="lazy"
                decoding="async"
              />
            ) : (
              <div className="w-full h-full bg-obsidian2 flex items-center justify-center" data-testid={`cover-placeholder-${tour.slug}`}>
                <Compass className="h-10 w-10 text-copper/50" />
              </div>
            )}
          </div>
          <div className="absolute top-3 left-3">
            <span className={badgeClass(tour.badge)} data-testid={`badge-${tour.slug}`}>{t(`badge.${tour.badge}`)}</span>
          </div>
          <div className="absolute inset-0 bg-gradient-to-t from-obsidian/70 via-transparent to-transparent pointer-events-none" />
          <div className="absolute bottom-3 left-3 right-3 flex items-center justify-between font-mono text-[10px] uppercase tracking-[0.2em] text-cream2">
            <span>{lt.location}, {lt.country}</span>
            <span>{tour.duration_days} {t("tours.days")}</span>
          </div>
        </div>
        <div className="pt-4 flex items-start justify-between gap-4">
          <div>
            <h3 className="font-serif text-2xl leading-tight text-cream group-hover:text-copperlight transition-colors duration-300">{lt.title}</h3>
            <p className="font-mono text-[11px] text-sand mt-1">{lt.dates_label}</p>
          </div>
          <div className="text-right shrink-0">
            <p className="font-mono text-[10px] uppercase tracking-widest text-sand">{t("tours.from")}</p>
            <p className="font-serif text-xl text-copper">€{Number(tour.price).toLocaleString("it-IT")}</p>
          </div>
        </div>
        <p className="text-sm text-cream2/90 mt-2 leading-relaxed" style={{ display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}>
          {lt.excerpt}
        </p>
      </Link>
    </Reveal>
  );
}
